import { useCallback, useEffect, useRef, useState } from "react";
import { useReverification, useSession, useUser } from "@clerk/react";
import { hasSecondFactor } from "../lib/auth/second-factor";
import * as Sentry from "@sentry/react";
import {
  usePlaidLink,
  type PlaidLinkOnSuccess,
  type PlaidLinkOnSuccessMetadata,
  type PlaidLinkOnExit,
} from "react-plaid-link";

import { ApiError, isClerkReverificationHint } from "../lib/api";
import {
  createPlaidLinkToken,
  exchangePlaidPublicToken,
  syncPlaidItem,
  type PlaidExchangeRequest,
  type PlaidLinkTokenMode,
} from "../lib/plaid-api";

const PLAID_LINK_READY_TIMEOUT_MS = 20_000;

type PlaidLinkInitializationErrorCategory =
  "plaid_link_initialization_error" | "plaid_link_initialization_timeout";

/**
 * Result payload passed to `onLinked` once the bank is fully linked,
 * exchanged server-side, AND the initial sync has been attempted (or skipped
 * for income mode). Carries the institution name so call sites can render a
 * "Linked {bank}" toast or first-link-success modal without re-fetching items.
 */
export interface PlaidConnectLinkedResult {
  itemID: string;
  institutionName: string;
  /**
   * True when Plaid is still analyzing income transactions after the
   * exchange — IncomeService inserted a pending plaid_income_reports row
   * and the INCOME_VERIFICATION webhook will complete the fetch. The
   * "Verifying income…" hint stays on the row until then.
   * False for non-income flows AND for income flows where the backend
   * completed the fetch inline.
   */
  incomeProcessing: boolean;
}

export interface UsePlaidConnectOptions {
  /**
   * Resolves a Clerk session JWT. Accepts `{ skipCache: true }` so the
   * Plaid helpers — which are MFA-gated per ADR-022 — can request a
   * freshly minted token with the latest `fva` claim after a
   * reverification round-trip.
   */
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
  /**
   * Scopes the Plaid Link session. `"income"` requests
   * income_verification only — used by the per-row Income pill so the
   * user is only asked for permissions the row actually needs.
   * Omit (or pass `"initial"`) for the default auth + liabilities flow.
   */
  mode?: PlaidLinkTokenMode;
  /**
   * Fires after Plaid Link succeeds, the public_token has been exchanged
   * server-side for an internal item id, AND the initial /sync has been
   * attempted so balances/liabilities are available without a manual
   * refresh.
   */
  onLinked?: (result: PlaidConnectLinkedResult) => void;
  /**
   * Fires when token minting or the server-side exchange fails. Plaid
   * Link's own onExit (user cancelled) is NOT routed here — that's
   * normal flow. Reverification "Confirm MFA, then try again" hints are
   * also not surfaced here — Clerk's useReverification() handles those
   * via its focused secure-prompt modal.
   */
  onError?: (err: unknown) => void;
  /** Normal Plaid cancellation; callers can restore their connect action. */
  onExit?: () => void;
  /**
   * Called when the backend returns `mfa_required` with
   * `requires_second_factor_setup=true` and the user does NOT yet have an
   * authenticator app enrolled. Call sites should keep the user in the
   * attempted Plaid flow and offer the Sill-owned authenticator setup UI.
   * Per ADR-022, Plaid must not pop the Clerk UserProfile modal for setup.
   */
  onSecondFactorSetupRequired?: () => void;
  /**
   * Caller-owned view of whether the user has enrolled an authenticator app.
   * Settings can optimistically flip this after local TOTP enrollment before
   * Clerk's useUser() snapshot updates, allowing the immediate retry to use
   * Clerk reverification instead of falling back into setup-required handling.
   */
  secondFactorEnrolled?: boolean;
  /** Funnel-only D15 carve-out. Transactions mint/exchange are free and
   * precede MFA enrollment; ongoing Plaid surfaces must leave this false. */
  verificationFunnel?: boolean;
}

export interface UsePlaidConnectResult {
  /**
   * Triggers the full connect flow:
   *   1. Mint a link token (server side, scoped to `mode`, MFA-gated).
   *   2. Hand it to Plaid Link.
   *   3. When Plaid Link is ready, open the modal.
   *   4. On Plaid success, exchange the public_token for an internal
   *      item id, then trigger an immediate /sync so liabilities pull
   *      before `onLinked` fires.
   * Safe to call multiple times — each call fetches a fresh token
   * (link tokens are single-use).
   */
  open: () => Promise<void>;
  /** True while the link token is minted and Plaid Link prepares to open. */
  isLoading: boolean;
  /**
   * True between Plaid Link's onSuccess and our own onLinked: the exchange
   * + initial sync round-trip. Call sites use this to render a "Linking
   * your bank…" indicator — without it, the user sees Plaid Link close
   * and then nothing happens for 1-3s while sync runs.
   */
  isLinking: boolean;
  /**
   * Institution name captured the moment Plaid Link fires onSuccess, so
   * the linking-progress UI can read "Linking Chase…" instead of a
   * generic spinner. Null when isLinking is false.
   */
  linkingInstitutionName: string | null;
  error: string | null;
}

/**
 * usePlaidConnect wraps react-plaid-link's usePlaidLink with the Sill
 * server-side token-mint + exchange flow. Callers don't see the link
 * token — they get an `open()` button and an `onLinked({itemID, ...})`
 * event once the bank is fully connected, persisted, AND its initial
 * sync has been attempted.
 *
 * Per ADR-022, every Plaid HTTP call here is MFA-gated. The helpers are
 * wrapped in Clerk's `useReverification()` so a stale `fva` claim pops
 * the focused secure prompt and the request auto-retries afterward — no
 * stranded "Confirm MFA, then try again" toast that requires the user to
 * route through View Banks just to refresh their factor age.
 */
export function usePlaidConnect(
  opts: UsePlaidConnectOptions,
): UsePlaidConnectResult {
  const {
    getToken,
    mode,
    onLinked,
    onError,
    onExit,
    onSecondFactorSetupRequired,
    secondFactorEnrolled,
    verificationFunnel = false,
  } = opts;
  const { session } = useSession();
  const { user } = useUser();
  const authenticatorEnabled = secondFactorEnrolled ?? hasSecondFactor(user);
  // SILL-539: there is no plan gate here. Bank connection and sync are part
  // of the Free experience, so every authenticated consumer reaches the
  // link-token mint. The hook previously short-circuited non-Plus callers into
  // the GlobalUpgradeSheet; that path — and the `isPlus` field it exposed for
  // lock badges — are gone. Do not reintroduce them.
  //
  // MFA (ADR-022) is still enforced, both here via useReverification and on
  // the server. That is a security gate, not a paid one.

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [linkingInstitutionName, setLinkingInstitutionName] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  // Coordinates the "fetched token → wait for Plaid Link's ready →
  // call plaid.open()" handoff. A ref (not state) so the effect that
  // flushes it doesn't need to set state inside an effect — react-hooks
  // lints that pattern and the queue value never needs to drive a
  // re-render on its own.
  const openWhenReadyRef = useRef(false);
  const readinessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearReadinessTimeout = useCallback(() => {
    if (readinessTimeoutRef.current !== null) {
      clearTimeout(readinessTimeoutRef.current);
      readinessTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearReadinessTimeout();
    };
  }, [clearReadinessTimeout]);

  // Mint a fresh JWT for each Plaid call so reverification retries carry
  // the latest `fva` claim (ADR-022). Reload the session first so its
  // cached claims reflect any second-factor verification the user just
  // completed in Clerk's secure prompt.
  const getFreshToken = useCallback(
    async (options?: { skipCache?: boolean }) => {
      if (!session) {
        return getToken(options);
      }
      await session.reload().catch(() => undefined);
      const token = await session.getToken({
        skipCache: options?.skipCache ?? true,
      });
      return token ?? (await getToken(options));
    },
    [getToken, session],
  );

  // Each helper is wrapped in useReverification so a stale `fva` returns
  // a hint shape Clerk knows how to recover from. Refs avoid a stale
  // closure if the wrapped function identity changes between mount and
  // the moment Plaid Link fires onSuccess.
  const verifiedCreateLinkToken = useReverification(
    (linkMode?: PlaidLinkTokenMode) =>
      verificationFunnel
        ? createPlaidLinkToken(getFreshToken, { mode: linkMode })
        : createPlaidLinkToken(getFreshToken, {
            mode: linkMode,
            reverification: true,
            secondFactorEnrolled: authenticatorEnabled,
          }),
  );
  const verifiedExchange = useReverification((req: PlaidExchangeRequest) =>
    verificationFunnel
      ? exchangePlaidPublicToken(getFreshToken, req)
      : exchangePlaidPublicToken(getFreshToken, req, {
          reverification: true,
          secondFactorEnrolled: authenticatorEnabled,
        }),
  );
  const verifiedSync = useReverification((itemID: string) =>
    syncPlaidItem(getFreshToken, itemID, {
      reverification: true,
      secondFactorEnrolled: authenticatorEnabled,
    }),
  );

  // Hold the latest "verified" handles in refs so handleSuccess (which
  // is registered with Plaid Link once at hook construction) always
  // closes over the most recent identities. Without this, a successful
  // Plaid Link flow that finishes after a re-render would call into a
  // stale verifiedExchange instance.
  const verifiedExchangeRef = useRef(verifiedExchange);
  const verifiedSyncRef = useRef(verifiedSync);
  useEffect(() => {
    verifiedExchangeRef.current = verifiedExchange;
    verifiedSyncRef.current = verifiedSync;
  }, [verifiedExchange, verifiedSync]);

  const handleSuccess: PlaidLinkOnSuccess = useCallback(
    (publicToken: string | null, metadata: PlaidLinkOnSuccessMetadata) => {
      // Drop the spent token immediately — link tokens are single-use,
      // and leaving it in state would let a later effect re-open with
      // an already-consumed token.
      setLinkToken(null);
      // react-plaid-link's newer typings admit a null public_token on the
      // success callback; without one there is nothing to exchange.
      if (!publicToken) {
        setError("Couldn't save the bank connection. Try again.");
        onError?.(new Error("Plaid returned no public token"));
        return;
      }
      const institutionID = metadata.institution?.institution_id ?? "";
      const institutionName = metadata.institution?.name ?? "";
      // Flip into the "linking" state right as Plaid Link's modal
      // closes — the exchange + sync round-trip below can take 1-3s in
      // sandbox, longer in prod, and the caller needs an explicit signal
      // to render a progress indicator. Without this, the UI shows a
      // stale page with no spinner during the window.
      setIsLinking(true);
      setLinkingInstitutionName(institutionName);
      // Run async work outside the synchronous Plaid callback so any
      // exception surfaces through our own error sink, not Plaid's.
      void (async () => {
        try {
          const exchangeResult = await verifiedExchangeRef.current({
            publicToken,
            institutionID,
            institutionName,
            // The backend derives consent_scopes server-side from `mode`
            // (via ScopesForMode in services/plaid/link.go) — the wire
            // form mirrors the link-token-mint endpoint so a stale
            // frontend can't stamp ungranted products into the audit
            // trail. Defaults to "initial" when undefined.
            mode: mode ?? "initial",
          });
          if (isClerkReverificationHint(exchangeResult)) {
            // Reverification path is owned by Clerk's modal; on the
            // rare case where the SDK couldn't recover, surface a
            // retry-able error rather than silently dropping the link.
            setError("Confirm MFA, then try again.");
            return;
          }
          const { itemID, incomeProcessing } = exchangeResult;
          // Income mode does NOT trigger sync: IncomeService already handled the
          // fetch + persist + snapshot refresh on the backend, and AccountsBalanceGet/
          // LiabilitiesGet aren't applicable to income-only items (wasted spend + noise).
          if (mode !== "income" && !verificationFunnel) {
            // Auto-sync after exchange so balances + liabilities are
            // visible without a manual Refresh click. Sync failure here is
            // non-fatal — the item is already persisted, the user can
            // Refresh manually later — so we log via onError and continue.
            try {
              const syncResult = await verifiedSyncRef.current(itemID);
              if (isClerkReverificationHint(syncResult)) {
                // Sync reverification miss is non-fatal: the item is
                // linked. onLinked still fires so the success toast/modal
                // surfaces; the readiness refresh will reflect liabilities
                // once the user clicks Refresh.
                onError?.(new Error("plaid sync reverification miss"));
              }
            } catch (syncErr) {
              onError?.(syncErr);
            }
          }
          onLinked?.({ itemID, institutionName, incomeProcessing });
        } catch (err) {
          if (isSecondFactorSetupRequired(err)) {
            onSecondFactorSetupRequired?.();
            if (!onSecondFactorSetupRequired) {
              setError("Set up an authenticator app, then try again.");
            }
            return;
          }
          setError("Couldn't save the bank connection. Try again.");
          onError?.(err);
        } finally {
          // Always clear the linking state on the way out — success,
          // reverification hint, error, setup-required. The caller's
          // own state (success modal, error toast) takes over from
          // here; leaving isLinking true would strand the "Linking…"
          // dialog open.
          setIsLinking(false);
          setLinkingInstitutionName(null);
        }
      })();
    },
    [mode, onLinked, onError, onSecondFactorSetupRequired, verificationFunnel],
  );

  const handleExit: PlaidLinkOnExit = useCallback(
    (err) => {
      openWhenReadyRef.current = false;
      clearReadinessTimeout();
      setLinkToken(null);
      setIsLoading(false);
      setIsLinking(false);
      setLinkingInstitutionName(null);
      if (err) {
        setError("Couldn't complete the bank connection. Try again.");
        onError?.(err);
      } else {
        setError(null);
        onExit?.();
      }
    },
    [clearReadinessTimeout, onError, onExit],
  );

  const plaid = usePlaidLink({
    token: linkToken,
    onSuccess: handleSuccess,
    onExit: handleExit,
  });

  const completePlaidOpen = useCallback(
    (err?: unknown, category?: PlaidLinkInitializationErrorCategory) => {
      clearReadinessTimeout();
      if (category) {
        reportPlaidLinkInitializationFailure(category);
      }
      queueMicrotask(() => {
        if (!mountedRef.current) return;
        if (err) {
          setLinkToken(null);
          setError("Couldn't open the bank connection. Try again.");
          onError?.(err);
        }
        setIsLoading(false);
      });
    },
    [clearReadinessTimeout, onError],
  );

  useEffect(() => {
    if (!openWhenReadyRef.current || linkToken === null) return;

    // SILL-544. react-plaid-link reports an initialization failure through
    // `error` (typed ErrorEvent — the usual cause is the Link script from
    // cdn.plaid.com failing to load) and leaves `ready` false FOREVER. This
    // branch used to read only `ready`, so such a failure fired nothing:
    // `completePlaidOpen` — the sole path that clears isLoading on this
    // effect — was never reached, no message was set, and the buyer's first
    // "Verify with bank" click silently did nothing. Clicking again re-minted
    // and re-initialized, which is why a retry appeared to "just work".
    //
    // Checked BEFORE `ready` so a library that surfaces both never opens a
    // Link session it has already reported as broken.
    if (plaid.error) {
      openWhenReadyRef.current = false;
      completePlaidOpen(plaid.error, "plaid_link_initialization_error");
      return;
    }

    if (plaid.ready) {
      openWhenReadyRef.current = false;
      clearReadinessTimeout();
      try {
        plaid.open();
      } catch (err) {
        completePlaidOpen(err);
        return;
      }
      completePlaidOpen();
    }
  }, [clearReadinessTimeout, completePlaidOpen, linkToken, plaid]);

  const open = useCallback(async () => {
    setError(null);
    setLinkToken(null);
    openWhenReadyRef.current = false;
    clearReadinessTimeout();
    setIsLoading(true);
    try {
      const token = await verifiedCreateLinkToken(mode);
      if (isClerkReverificationHint(token)) {
        setError("Confirm MFA, then try again.");
        setIsLoading(false);
        return;
      }
      openWhenReadyRef.current = true;
      setLinkToken(token);
      readinessTimeoutRef.current = setTimeout(() => {
        if (!openWhenReadyRef.current) return;
        openWhenReadyRef.current = false;
        completePlaidOpen(
          new Error("Plaid Link did not become ready before the timeout."),
          "plaid_link_initialization_timeout",
        );
      }, PLAID_LINK_READY_TIMEOUT_MS);
    } catch (err) {
      if (isSecondFactorSetupRequired(err)) {
        onSecondFactorSetupRequired?.();
        if (!onSecondFactorSetupRequired) {
          setError("Set up an authenticator app, then try again.");
        }
        setIsLoading(false);
        return;
      }
      setError("Couldn't start the bank connection. Try again.");
      onError?.(err);
      setIsLoading(false);
    }
  }, [
    clearReadinessTimeout,
    completePlaidOpen,
    verifiedCreateLinkToken,
    mode,
    onError,
    onSecondFactorSetupRequired,
  ]);

  return { open, isLoading, isLinking, linkingInstitutionName, error };
}

/**
 * Sentry receives only a fixed message and category. A link token, institution,
 * or library ErrorEvent may be sensitive, so none is attached to telemetry.
 */
function reportPlaidLinkInitializationFailure(
  category: PlaidLinkInitializationErrorCategory,
): void {
  Sentry.withScope((scope) => {
    scope.setTag("error_category", category);
    Sentry.captureMessage("Plaid Link initialization failed", "warning");
  });
}

function isSecondFactorSetupRequired(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.code === "mfa_required" &&
    err.payload?.requires_second_factor_setup === true
  );
}
